import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  Plus,
  Search,
  Trash2,
  Edit,
  Clock,
  Calendar,
  Users,
  Tag,
  Filter,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import axios from "axios";
import api from "../utils/axios";
import { toast } from "../utils/toastUtils";

const OrderscheduleList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeSlotDurationFilter, setTimeSlotDurationFilter] = useState("all");
  const [totalSlotsFilter, setTotalSlotsFilter] = useState("all");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Fetch all schedules
  const { data, isLoading, error } = useQuery(
    ["order-schedule", page, searchTerm, statusFilter, timeSlotDurationFilter, totalSlotsFilter],
    async () => {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== "all") params.status = statusFilter;
      if (timeSlotDurationFilter !== "all") params.time_slot_duration = timeSlotDurationFilter;
      if (totalSlotsFilter !== "all") params.total_slots = totalSlotsFilter;
      
      const response = await api.get("/api/order-schedule", {
        params: {
          page,
          limit: 20,
          ...params,
        },
      });
      return response.data;
    },
    {
      refetchInterval: 30000,
      retry: 3,
      retryDelay: 1000,
      onError: (error) => {
        console.error("Orderschedule query error:", error);
      },
    }
  );

  // Delete schedule mutation
  const deleteMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/api/order-schedule/${id}`);
      return response.data;
    },
    {
      onMutate: async (id) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries([
          "order-schedule", 
          page, 
          searchTerm, 
          statusFilter, 
          timeSlotDurationFilter, 
          totalSlotsFilter
        ]);

        // Snapshot the previous value
        const previousData = queryClient.getQueryData([
          "order-schedule",
          page,
          searchTerm,
          statusFilter,
          timeSlotDurationFilter,
          totalSlotsFilter,
        ]);

        // Optimistic update - remove from cache immediately
        queryClient.setQueryData(
          ["order-schedule", page, searchTerm, statusFilter, timeSlotDurationFilter, totalSlotsFilter],
          (oldData) => {
            if (!oldData) return oldData;
            
            // Check the actual structure of your data
            console.log("Old data structure:", oldData);
            
            // Based on your component usage, data has order_schedules at root
            // But let's make it more robust by checking different possible structures
            const orderSchedules = oldData.order_schedules || oldData.data?.order_schedules || [];
            const pagination = oldData.pagination || oldData.data?.pagination || { total: 0 };
            
            if (Array.isArray(orderSchedules)) {
              // If data.order_schedules exists at root
              if (oldData.order_schedules) {
                return {
                  ...oldData,
                  order_schedules: orderSchedules.filter(schedule => schedule.id !== id),
                  pagination: {
                    ...pagination,
                    total: pagination.total - 1,
                  },
                };
              } 
              // If data.data.order_schedules exists (nested structure)
              else if (oldData.data && oldData.data.order_schedules) {
                return {
                  ...oldData,
                  data: {
                    ...oldData.data,
                    order_schedules: orderSchedules.filter(schedule => schedule.id !== id),
                    pagination: {
                      ...pagination,
                      total: pagination.total - 1,
                    },
                  },
                };
              }
            }
            
            return oldData;
          }
        );

        return { previousData };
      },
      onSuccess: () => {
        // Show success message
        toast.success("Order schedule deleted successfully!");
      },
      onError: (error, id, context) => {
        // Rollback optimistic update on error
        if (context?.previousData) {
          queryClient.setQueryData(
            ["order-schedule", page, searchTerm, statusFilter, timeSlotDurationFilter, totalSlotsFilter],
            context.previousData
          );
        }
        
        const errorMessage =
          error.response?.data?.message || "Failed to delete order schedule";
        toast.error(errorMessage);
      },
      onSettled: () => {
        // Refresh data
        queryClient.invalidateQueries(["order-schedule"]);
      },
    }
  );

  // Toggle schedule active status mutation
  const toggleStatusMutation = useMutation(
    async ({ id, is_active }) => {
      const response = await axios.patch(`/api/order-schedule/${id}`, {
        is_active,
      });
      return response.data;
    },
    {
      onMutate: async ({ id, is_active }) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(["order-schedule"]);

        // Snapshot the previous value
        const previousData = queryClient.getQueryData([
          "order-schedule",
          page,
          searchTerm,
          statusFilter,
          timeSlotDurationFilter,
          totalSlotsFilter,
        ]);

        // Optimistically update to the new value
        queryClient.setQueryData(
          ["order-schedule", page, searchTerm, statusFilter, timeSlotDurationFilter, totalSlotsFilter],
          (oldData) => {
            if (!oldData) return oldData;
            
            // Helper function to update schedules in any structure
            const updateSchedules = (schedules) => {
              return schedules.map((schedule) =>
                schedule.id === id
                  ? { ...schedule, is_active: !is_active }
                  : schedule
              );
            };
            
            // Check structure and update accordingly
            if (oldData.order_schedules) {
              return {
                ...oldData,
                order_schedules: updateSchedules(oldData.order_schedules),
              };
            } else if (oldData.data && oldData.data.order_schedules) {
              return {
                ...oldData,
                data: {
                  ...oldData.data,
                  order_schedules: updateSchedules(oldData.data.order_schedules),
                },
              };
            }
            
            return oldData;
          }
        );

        return { previousData };
      },
      onError: (err, { id, is_active }, context) => {
        // Rollback on error
        if (context?.previousData) {
          queryClient.setQueryData(
            ["order-schedule", page, searchTerm, statusFilter, timeSlotDurationFilter, totalSlotsFilter],
            context.previousData
          );
        }
        
        const errorMessage =
          err.response?.data?.message || "Failed to update schedule status";
        toast.error(errorMessage);
      },
      onSettled: () => {
        // Always refetch after error or success
        queryClient.invalidateQueries(["order-schedule"]);
      },
    }
  );

  const handleDelete = (id, vendorId) => {
    if (
      window.confirm(
        `Are you sure you want to delete order schedule for Vendor ID: ${vendorId}?`
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleStatus = (id, currentStatus) => {
    toggleStatusMutation.mutate({ id, is_active: currentStatus });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    const time = new Date(`2000-01-01T${timeString}`);
    return time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDurationOptions = () => {
    return [15, 30, 60, 90, 120];
  };

  const getTotalSlotsOptions = () => {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTimeSlotDurationFilter("all");
    setTotalSlotsFilter("all");
    setPage(1);
  };

  const hasActiveFilters =
    searchTerm ||
    statusFilter !== "all" ||
    timeSlotDurationFilter !== "all" ||
    totalSlotsFilter !== "all";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Calendar className="h-8 w-8 text-red-400" />
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          Failed to load order schedules
        </h3>
        <p className="text-secondary-600 mb-4">
          {error.response?.data?.message ||
            error.message ||
            "An error occurred while loading order schedules"}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Extract data based on actual structure
  console.log('Data structure:', data);
  
  // Check different possible structures
  let order_schedules = [];
  let pagination = {};
  
  if (data) {
    if (Array.isArray(data.order_schedules)) {
      order_schedules = data.order_schedules;
      pagination = data.pagination || {};
    } else if (data.data && Array.isArray(data.data.order_schedules)) {
      order_schedules = data.data.order_schedules;
      pagination = data.data.pagination || {};
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Order Schedules</h1>
        <p className="text-secondary-600">
          Configure time slots, and monitor scheduled order slots.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-secondary-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-3 md:space-y-0">
          <h3 className="text-base md:text-lg font-semibold text-secondary-900">
            Search & Filters
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-red-600 hover:text-red-700 underline self-start md:self-auto"
            >
              Clear all filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="text"
              placeholder="Search by Schedule ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors text-sm md:text-base"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors text-sm md:text-base"
          >
            <option value="all">All Status</option>
            <option value="active">✅ Active</option>
            <option value="inactive">❌ Inactive</option>
          </select>

          <select
            value={timeSlotDurationFilter}
            onChange={(e) => {
              setTimeSlotDurationFilter(e.target.value);
              setPage(1);
            }}
            className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors text-sm md:text-base"
          >
            <option value="all">All Durations</option>
            {getDurationOptions().map((duration) => (
              <option key={duration} value={duration}>
                ⏱️ {duration} minutes
              </option>
            ))}
          </select>

          <select
            value={totalSlotsFilter}
            onChange={(e) => {
              setTotalSlotsFilter(e.target.value);
              setPage(1);
            }}
            className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors text-sm md:text-base"
          >
            <option value="all">All Total Slots</option>
            {getTotalSlotsOptions().map((slot) => (
              <option key={slot} value={slot}>
                🕒 {slot} {slot === 1 ? "slot" : "slots"}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
          <div className="flex items-center space-x-2">
            {/* Optional filter toggle button */}
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-secondary-600">
               📊  {order_schedules.length} of {pagination.total || 0} schedules
            </span>
            <button
              onClick={() => navigate("/order-schedule/create")}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 shadow-sm hover:shadow-md transition-all duration-200 text-sm font-medium"
            >
              <Plus className="h-5 w-5" />
              <span>Create Schedule</span>
            </button>
          </div>
        </div>
      </div>

      {/* Orders List - Card View */}
      <div className="space-y-3 md:space-y-4">
        {order_schedules.length > 0 ? (
          order_schedules.map((schedule) => (
            <div
              key={schedule.id}
              className="bg-white rounded-lg border border-secondary-200 p-4 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="h-10 w-10 md:h-12 md:w-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Calendar className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1">
                      <h3 className="font-semibold text-secondary-900 text-sm md:text-base">
                        #{schedule.id}
                      </h3>
                      <button
                        onClick={() => handleToggleStatus(schedule.id, schedule.is_active)}
                        disabled={toggleStatusMutation.isLoading}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                          schedule.is_active
                            ? "bg-green-100 text-green-800 border-green-200 hover:bg-green-200"
                            : "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200"
                        } transition-colors`}
                      >
                        {toggleStatusMutation.isLoading ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : schedule.is_active ? (
                          "✅ Active"
                        ) : (
                          "❌ Inactive"
                        )}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3 mb-2">
                      <div className="flex items-center space-x-1 text-xs md:text-sm text-secondary-600">
                        <Clock className="h-3 w-3 md:h-4 md:w-4 text-blue-500" />
                        <span className="font-medium">{schedule.time_slot_duration} min slots</span>
                      </div>
                      <div className="flex items-center space-x-1 text-xs md:text-sm text-secondary-600">
                        <Users className="h-3 w-3 md:h-4 md:w-4 text-green-500" />
                        <span className="font-medium">{schedule.total_slots || 0} slots</span>
                      </div>
                      <div className="flex items-center space-x-1 text-xs md:text-sm text-secondary-600">
                        <Tag className="h-3 w-3 md:h-4 md:w-4 text-purple-500" />
                        <span className="font-medium">{schedule.total_capacity || 0} capacity</span>
                      </div>
                      <div className="flex items-center space-x-1 text-xs md:text-sm text-secondary-600">
                        <Clock className="h-3 w-3 md:h-4 md:w-4 text-orange-500" />
                        <span className="font-medium">{schedule.min_lead_time_minutes} min lead</span>
                      </div>
                    </div>

                    <div className="text-xs text-secondary-500 space-y-1">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        <span>
                          {formatDate(schedule.start_date)} → {formatDate(schedule.end_date)}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        <span>Updated: {formatDateTime(schedule.updated_at)}</span>
                      </div>
                    </div>

                    {/* Slots Preview */}
                    {schedule.slots && schedule.slots.length > 0 && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-1">
                          {schedule.slots.slice(0, 3).map((slot) => (
                            <div
                              key={slot.id}
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                                slot.slot_status === "Open"
                                  ? "bg-green-100 text-green-800 border border-green-200"
                                  : slot.slot_status === "Closed"
                                  ? "bg-red-100 text-red-800 border border-red-200"
                                  : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                              }`}
                            >
                              <Clock className="h-3 w-3 mr-1" />
                              <span>
                                {formatTime(slot.start_time)}-{formatTime(slot.end_time)}
                              </span>
                              <span className="ml-1 font-medium">({slot.slot_limit})</span>
                            </div>
                          ))}
                          {schedule.slots.length > 3 && (
                            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 border border-blue-200">
                              +{schedule.slots.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                  <div className="text-left sm:text-right">
                    <div className="flex flex-col space-y-1">
                      <p className="text-xs text-secondary-500">
                        Created: {formatDate(schedule.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2 self-start sm:self-auto">
                    <button
                      onClick={() => navigate(`/order-schedule/${schedule.id}`)}
                      className="group relative p-2 text-secondary-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4 md:h-5 md:w-5" />
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                        View Details
                      </div>
                    </button>
                    <button
                      onClick={() => navigate(`/order-schedule/${schedule.id}/edit`)}
                      className="group relative p-2 text-secondary-400 hover:text-yellow-600 rounded-lg hover:bg-yellow-50 transition-colors"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4 md:h-5 md:w-5" />
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                        Edit
                      </div>
                    </button>
                    <button
                      onClick={() => handleDelete(schedule.id, schedule.vendor_id)}
                      className="group relative p-2 text-secondary-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                      title="Delete"
                      disabled={deleteMutation.isLoading}
                    >
                      {deleteMutation.isLoading ? (
                        <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 md:h-5 md:w-5" />
                      )}
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                        Delete
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="h-16 w-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-secondary-400" />
            </div>
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              {searchTerm || statusFilter !== "all"
                ? "No schedules found"
                : "No order schedules yet"}
            </h3>
            <p className="text-secondary-600">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search terms or filters"
                : "Create your first order schedule to get started"}
            </p>
            {!searchTerm && statusFilter === "all" && (
              <button
                onClick={() => navigate("/order-schedule/create")}
                className="mt-4 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800"
              >
                Create First Schedule
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-white rounded-lg border border-secondary-200 p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
            <div className="text-sm text-secondary-700">
              Showing {((page - 1) * (pagination.limit || 20)) + 1} to{" "}
              {Math.min(page * (pagination.limit || 20), pagination.total)} of {pagination.total}{" "}
              results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  let pageNum = i + 1;
                  if (pagination.pages > 5) {
                    if (page > 3) {
                      pageNum = page - 2 + i;
                      if (pageNum > pagination.pages) pageNum = pagination.pages - (4 - i);
                    }
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 rounded-lg transition-colors ${
                        page === pageNum
                          ? "bg-primary-600 text-white"
                          : "border border-secondary-300 hover:bg-secondary-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {pagination.pages > 5 && <span className="px-2 text-secondary-500">...</span>}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                disabled={page >= pagination.pages}
                className="p-2 border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderscheduleList;